import * as React from "react";
import { Check, ChevronsUpDown, Redo2, GripVertical, X, CircleAlert } from "lucide-react";
import { cn, sanitizeHtml } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "../ui/badge";
import { useEffect, useState } from "react";
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { SortableItem } from "../ui/sortable-item";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { useFieldContext } from "./form-context";
import { OverridesButton } from "../overrides-button";

export type Option = {
  value: number | string;
  label: string;
};

export function MultiSelectComboboxSortable({
  options,
  placeholder,
  selected = [],
  onChange,
  className,
  description,
  id,
  label,
  htmlContent,
  defaultSystemValue,
  isSystemSettings = false,
  openOverridesModal,
  domain,
}: {
  options: Option[];
  placeholder: string;
  selected: (number | string)[] | string;
  onChange: React.Dispatch<React.SetStateAction<(number | string)[] | string>>;
  className?: string;
  description?: string;
  id: string;
  label: React.ReactNode;
  htmlContent?: string;
  defaultSystemValue?: string;
  isSystemSettings?: boolean;
  openOverridesModal?: () => void;
  domain?: string;
}) {
  const [open, setOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<(number | string)[]>([]);
  const sanitizedHtml = htmlContent ? sanitizeHtml(htmlContent) : null;
  const [sortedOptions, setSortedOptions] = useState<Option[]>([]);
  const value = useFieldContext<string>().state.value ?? "";

  useEffect(() => {
    const selectedOrderMap = new Map<string, number>(selectedValues.map((v, i) => [String(v), i]));
    const originalIndexMap = new Map<string, number>(options.map((opt, i) => [String(opt.value), i]));
    const next = [...options].sort((a, b) => {
      const aKey = String(a.value),
        bKey = String(b.value);
      const aIdx = selectedOrderMap.has(aKey) ? selectedOrderMap.get(aKey)! : Number.POSITIVE_INFINITY;
      const bIdx = selectedOrderMap.has(bKey) ? selectedOrderMap.get(bKey)! : Number.POSITIVE_INFINITY;
      if (aIdx !== bIdx) return aIdx - bIdx;
      return (originalIndexMap.get(aKey) as number) - (originalIndexMap.get(bKey) as number);
    });
    setSortedOptions(next);
  }, [options, selectedValues]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  useEffect(() => {
    if (typeof selected === "string") {
      setSelectedValues(selected.split(",").map((item) => item.trim()));
    } else {
      setSelectedValues(selected);
    }
  }, [selected]);

  const handleUnselect = (item: number | string) => {
    onChange(selectedValues.filter((i) => i !== item));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = selectedValues.indexOf(active.id as string);
      const newIndex = selectedValues.indexOf(over.id as string);
      const newSelectedValues = arrayMove(selectedValues, oldIndex, newIndex);
      onChange(newSelectedValues);
    }
  };

  const handleSelect = (optionValue: number | string) => {
    if (selectedValues.includes(optionValue)) {
      onChange(selectedValues.filter((item) => item !== optionValue));
    } else {
      onChange([...selectedValues, optionValue]);
    }
    setOpen(true);
  };

  let field: ReturnType<typeof useFieldContext<string>> | null = null;
  try {
    field = useFieldContext<string>();
  } catch (e) {
    field = null;
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-center gap-2">
        {label && <div className="text-sm leading-none font-medium">{label}</div>}
        {defaultSystemValue !== undefined && value !== defaultSystemValue && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <CircleAlert className="w-4 h-4 text-orange-500" />
              </TooltipTrigger>
              <TooltipContent>
                <p>This value overwrites the default system value: {defaultSystemValue}</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  className="text-orange-500 pb-0 pt-0 mr-auto"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    field?.setValue(defaultSystemValue ?? "");
                  }}
                >
                  <Redo2 className="w-2 h-2" />
                  Reset to default
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Reset the value to the default system value: {defaultSystemValue}</p>
              </TooltipContent>
            </Tooltip>
          </>
        )}
        {isSystemSettings && domain !== "SYSTEM" && <OverridesButton onClick={openOverridesModal!} />}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between bg-transparent"
          >
            {placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-(--radix-popover-trigger-width) p-0">
          <Command>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={selectedValues} strategy={verticalListSortingStrategy}>
                <CommandList>
                  <CommandEmpty>No item found.</CommandEmpty>
                  <CommandGroup>
                    {sortedOptions.map((option) => (
                      <SortableItem
                        key={option.value}
                        id={option.value}
                        onWrapperClick={() => handleSelect(option.value)}
                      >
                        <CommandItem
                          key={option.value}
                          value={option.label}
                          className="cursor-grab"
                          onSelect={() => {
                            handleSelect(option.value);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedValues.includes(option.value) ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {option.label}
                          <GripVertical
                            className={cn(
                              "ml-auto h-4 w-4 opacity-50",
                              selectedValues.includes(option.value) ? "opacity-100" : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      </SortableItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </SortableContext>
            </DndContext>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedValues
            .map((item) => sortedOptions.find((option) => String(option.value) === String(item)))
            .filter((option): option is Option => option !== undefined)
            .map((option) => (
              <Badge variant="secondary" key={option.value} className="flex items-center gap-2">
                {option.label}
                <button
                  className="rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={() => handleUnselect(option.value)}
                >
                  <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </button>
              </Badge>
            ))}
        </div>
      )}

      {description && <div className="text-xs form-description">{description}</div>}
      {htmlContent && (
        <div className="text-xs form-description" dangerouslySetInnerHTML={{ __html: sanitizedHtml || "" }} />
      )}
    </div>
  );
}
